/**
 * Post-prepare decision for the Film-Print station's `prepareAndDropUnitCore`
 * (BLU-233 / phase 50.1 plan 06). Extracted as a small, dependency-injected,
 * pure-ish function so the two early-return branches — shelf-claim and the
 * plan 04 combined-composite fail-closed gate — can be behaviour-tested
 * without rendering the 2000+-line `FilmPrintPage` component.
 *
 * `prepareAndDropUnitCore` calls this immediately after resolving `prep` and
 * switches on the returned `kind`:
 *   - `'shelfClaim'`   — a PRE-PRINTED shelf film was auto-claimed; nothing
 *                        drops to the agent. The component still owns the
 *                        shelf-slot bookkeeping (setShelfClaims/invalidate/
 *                        outcomes.record) — this function only sets the unit
 *                        state, matching its pre-extraction behaviour.
 *   - `'combinedFailure'` — the backend could not build/transport a required
 *                        combined front+back film (plan 04's fail-closed
 *                        gate). Nothing drops to the agent. This function
 *                        routes it through the shared `recordNonDelivery`
 *                        triple (audit + operator warning + needs-manual
 *                        flag) and sets the unit to the retryable `'error'`
 *                        state.
 *   - `'proceedToDrop'` — neither flag is set; the component continues into
 *                        the existing agent-drop branch unchanged.
 *
 * Post-impl review (50.1-REVIEWS.md) added the retry/escalation vocabulary
 * below: `classifyPrepareAttempt` + `decideNextAttempt` are the two pure
 * functions the station's attempt loop switches on, so "retry twice more, then
 * file an issue" is unit-testable without a rendered station.
 */

export type PrepareOutcomeKind = 'shelfClaim' | 'combinedFailure' | 'proceedToDrop';

/**
 * What the station's attempt loop should do with a finished attempt.
 *   - `'settle'`    — this attempt is FINAL. Either something reached the RIP
 *                     (or the shelf), or it failed in a way the retry ladder
 *                     must not touch: the agent-side exits (drop failed, no
 *                     payload, no hot folder) already leave a real film row
 *                     plus the manual "Mark Film Done" path, and re-running
 *                     `prepareFilm` cannot fix a hot folder that isn't picked.
 *   - `'retryable'` — NOTHING reached the RIP, but another attempt has a real
 *                     chance: the backend fires its mirror-heal on every miss,
 *                     so a delayed retry often lands because the heal just did.
 *   - `'escalate'`  — NOTHING reached the RIP and retrying cannot help: file
 *                     the issue now instead of burning the ladder.
 */
export type PrepareAttemptClass = 'settle' | 'retryable' | 'escalate';

export interface PrepareOutcomeResult {
  kind: PrepareOutcomeKind;
  /** Retry/escalate class for the station's attempt loop (see `classifyPrepareAttempt`). */
  attemptClass: PrepareAttemptClass;
}

/** The subset of `PrepareFilmResult` this decision needs. */
export interface PrepareOutcomeInput {
  claimedFromShelf?: boolean;
  combinedCompositeFailed?: boolean;
  filename?: string | null;
}

/**
 * Every signal the attempt loop can hold about ONE finished prepare attempt —
 * two from the RESULT (`prepareFilm` resolved), two from a THROW. Booleans, not
 * a tRPC error, so this module stays dependency-free and directly testable;
 * the component does the `trpc-error-utils` parsing and passes the answers in.
 */
export interface PrepareAttemptSignals {
  /** `prep.combinedCompositeFailed` — no drop payload at all (plan 04 gate). */
  combinedCompositeFailed?: boolean;
  /** `prep.artworkMissing` — proven-absent artwork. Retryable on purpose: the
   *  backend fires its confirmed-absence mirror-heal on every miss, so the
   *  next attempt often finds the file the heal just wrote. */
  artworkMissing?: boolean;
  /** Threw with a `FILM_ARTWORK_TRANSIENT` cause (`retryable !== false`). */
  artworkTransient?: boolean;
  /** Threw with an artwork cause the backend tagged `retryable: false` —
   *  corrupt dual row / validation. Never retried. */
  artworkPermanent?: boolean;
}

/**
 * Classify ONE finished attempt. Precedence is deliberate: a PERMANENT signal
 * outranks every retryable one, because a corrupt dual row that also reports a
 * missing side must not be retried three times before anyone is told.
 */
export function classifyPrepareAttempt(signals: PrepareAttemptSignals): PrepareAttemptClass {
  if (signals.artworkPermanent) return 'escalate';
  if (signals.combinedCompositeFailed || signals.artworkMissing || signals.artworkTransient) {
    return 'retryable';
  }
  return 'settle';
}

/** Total prepare attempts per unit, INCLUDING the operator's own tap (Brett's
 *  spec: "auto-retry up to 3 TOTAL attempts"). */
export const MAX_PREPARE_ATTEMPTS = 3;

/** Delay between auto-retries. Long enough for the backend's mirror-heal write
 *  to land (the batch-1688 "PNG not mirrored yet" shape), short enough that the
 *  operator watches one card rather than walking away. */
export const PREPARE_RETRY_DELAY_MS = 4000;

/** What the loop does next after an attempt of a given class. */
export type AttemptDecision = 'settle' | 'retry' | 'escalate';

/**
 * The whole retry ladder, as one pure function.
 *   settle                        → settle (never retried, never escalated)
 *   escalate                      → escalate immediately
 *   retryable, attempts remain    → retry
 *   retryable, ladder exhausted   → escalate
 */
export function decideNextAttempt(args: {
  attemptClass: PrepareAttemptClass;
  /** 1-based index of the attempt that just finished. */
  attempt: number;
  maxAttempts?: number;
}): AttemptDecision {
  const maxAttempts = args.maxAttempts ?? MAX_PREPARE_ATTEMPTS;
  if (args.attemptClass === 'settle') return 'settle';
  if (args.attemptClass === 'escalate') return 'escalate';
  return args.attempt < maxAttempts ? 'retry' : 'escalate';
}

export interface PrepareOutcomeDeps {
  /** Sets THIS unit's `UnitFilmState`. Only ever called with `'filmPrinted'`
   * (shelf-claim) or `'error'` (combined failure) by this function — never
   * `'preparing'` or `'printing'`. */
  setUnitState: (state: 'filmPrinted' | 'error') => void;
  /**
   * The shared "nothing reached the RIP" triple — `recordNonDelivery` already
   * bound to this garment by the caller (see `record-non-delivery.ts`). Audits
   * the failure, records the operator line / raises the single-run modal, AND
   * flags the unit for manual attention.
   *
   * Blocker A (post-impl review) lived exactly here: the combined-failure exit
   * used to audit + warn but never flag, so a refetch repainted the failed unit
   * "At RIP" and "Mark all done" swept it to FILM_PRINTED with no film. Going
   * through the triple makes that omission unrepresentable.
   */
  recordNonDelivery: (spec: {
    auditReason: string;
    warnTitleKey: string;
    warnMessage: string;
    filename?: string | null;
    severity?: 'error' | 'warning';
    logKey: string;
  }) => void;
  translate: (key: string) => string;
}

export function handlePrepareOutcome(prep: PrepareOutcomeInput, deps: PrepareOutcomeDeps): PrepareOutcomeResult {
  // BLU-233 pre-printed: prepareFilm auto-claimed a matching PRE-PRINTED
  // shelf film for this garment — it is already FILM_PRINTED server-side and
  // NOTHING drops to Fiery. Existing behaviour preserved: the unit state
  // becomes 'filmPrinted' immediately.
  if (prep.claimedFromShelf) {
    deps.setUnitState('filmPrinted');
    return { kind: 'shelfClaim', attemptClass: 'settle' };
  }

  // COMBINED-FILM FAIL-CLOSED (plan 04): this garment requires a single film
  // carrying BOTH designs, and the backend could not build or transport
  // one — all four drop fields were nulled server-side rather than falling
  // back to a silently one-sided film. Nothing is sent to the agent.
  if (prep.combinedCompositeFailed) {
    // The shared triple: audit (success:false, so a FILM_DROP_FAILED prod_event
    // exists even though nothing was attempted — batch 1672), warn the operator
    // by film, and flag the unit for manual attention.
    deps.recordNonDelivery({
      auditReason: 'combined film could not be built',
      filename: prep.filename ?? null,
      warnTitleKey: 'filmPrint.combinedCompositeFailedTitle',
      warnMessage: deps.translate('filmPrint.combinedCompositeFailedBody'),
      // Nothing reached the RIP and there is no film to rescue by hand — the
      // single-run modal is an ERROR, not a warning.
      severity: 'error',
      logKey: 'combined-composite-failed',
    });
    // 'error' — re-selectable, its button already reads Retry. NOT
    // 'preparing' (strands the unit with no control) and NOT 'printing'
    // (implies bytes reached the RIP, which is false here).
    deps.setUnitState('error');
    return { kind: 'combinedFailure', attemptClass: 'retryable' };
  }

  return { kind: 'proceedToDrop', attemptClass: 'settle' };
}
